import parse from 'html-react-parser';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { BoxImage, GridImage } from 'components/BoxImage';
import Image from 'components/Image';
import Loader from 'components/Loader';
import { ScrollNavigation } from 'components/ScrollNavigation';
import Section from 'components/Section';
import styled from 'styled-components';
import { colors } from 'styles/colors';
import { devices } from 'styles/devices';
import SidebarTemplate from 'templates/SidebarTemplate/SidebarTemplate';
import Headline from 'ui/Headline';
import SectionTwoCol from './SectionTwoCol';

export const SingleProduct = ({ loading, content, image, innerWrapperRef }) => {
   const intl = useIntl();

   if (loading) {
      return (
         <SidebarTemplate navigation="product">
            <Loader />
         </SidebarTemplate>
      );
   }

   return (
      <SidebarTemplate navigation="product">
         {content ? (
            <InnerWrapper ref={innerWrapperRef}>
               <Headline size="XL">{parse(content.title || '', { trim: true })}</Headline>
               <ScrollNavigation
                  sectionBeforeRef={innerWrapperRef}
                  scrollItems={[
                     !!content.konstrukcja_nosna && {
                        id: 'konstrukcja_nosna',
                        label: intl.formatMessage({ id: 'SingleProductView.SupportingStructure' })
                     },
                     (typeof content.wypelnienie === 'string'
                        ? !!content.wypelnienie
                        : !!content.wypelnienie?.wypelnienie_table ||
                          !!content.wypelnienie?.wypelnienie_text) && {
                        id: 'wypelnienie',
                        label: intl.formatMessage({ id: 'SingleProductView.Filling' })
                     },
                     !!content.elementy_konstrukcyjne && {
                        id: 'elementy_konstrukcyjne',
                        label: intl.formatMessage({ id: 'SingleProductView.ConstructionElements' })
                     },
                     !!content.informacje && {
                        id: 'informacje',
                        label: intl.formatMessage({ id: 'SingleProductView.Information' })
                     },
                     !!content.dlugosc_bram && {
                        id: 'dlugosc_bram',
                        label: intl.formatMessage({ id: 'SingleProductView.GateLength' })
                     },
                     !!content.wysokosc_bram__slupka && {
                        id: 'wysokosc_bram__slupka',
                        label: intl.formatMessage({
                           id: 'SingleProductView.PostsHeight'
                        })
                     },
                     !!content.przekroj_slupka && {
                        id: 'przekroj_slupka',
                        label: intl.formatMessage({ id: 'SingleProductView.PostCrossSection' })
                     },
                     !!content.ilosc_pol_w_swietle && {
                        id: 'ilosc_pol_w_swietle',
                        label: intl.formatMessage({ id: 'SingleProductView.LightNumberFields' })
                     },
                     !!content.przeciwwagi_do_bram?.length && {
                        id: 'przeciwwagi_do_bram',
                        label: intl.formatMessage({
                           id: 'SingleProductView.CounterweightStyle'
                        })
                     },
                     !!content.waga && {
                        id: 'waga',
                        label: intl.formatMessage({ id: 'SingleProductView.Weight' })
                     },
                     !!content.informator && {
                        id: 'informator',
                        label: intl.formatMessage({ id: 'SingleProductView.Informant' })
                     },
                     !!content.konstrukcja && {
                        id: 'konstrukcja',
                        label: intl.formatMessage({ id: 'SingleProductView.Construction' })
                     },
                     !!content.slup_z_automatem && {
                        id: 'slup_z_automatem',
                        label: intl.formatMessage({
                           id: 'SingleProductView.PoleWithAutomaticMachine'
                        })
                     },
                     !!content.podzial_slupkow && {
                        id: 'podzial_slupkow',
                        label: intl.formatMessage({ id: 'SingleProductView.BarsDivision' })
                     },
                     !!content.slupki_do_bram_uchylnych && {
                        id: 'slupki_do_bram_uchylnych',
                        label: intl.formatMessage({ id: 'SingleProductView.Poles' })
                     },
                     !!content.informacje_techniczne && {
                        id: 'informacje_techniczne',
                        label: intl.formatMessage({ id: 'SingleProductView.TechnicalInformations' })
                     },
                     !!content.montaz && {
                        id: 'montaz',
                        label: intl.formatMessage({ id: 'SingleProductView.Assembly' })
                     },
                     !!content.ilosc_nitow && {
                        id: 'ilosc_nitow',
                        label: intl.formatMessage({ id: 'SingleProductView.NumberOfRivets' })
                     },
                     !!content.rodzaje_przesel?.length && {
                        id: 'rodzaje_przesel',
                        label: intl.formatMessage({ id: 'SingleProductView.TypesOfSpans' })
                     },
                     !!content.rodzaje_przesel_z_opisem?.length && {
                        id: 'rodzaje_przesel_z_opisem',
                        label: intl.formatMessage({ id: 'SingleProductView.TypesOfSpans' })
                     },
                     !!content.akcesoria?.length && {
                        id: 'akcesoria',
                        label: intl.formatMessage({ id: 'SingleProductView.Accessories' })
                     }
                  ]}
               />
               {content.categories.includes(8) && (
                  <IntroWrapperEQ>
                     <StyledImage src={image} />
                     <StyledFirstContent>
                        <h2>
                           <FormattedMessage id="SingleProductView.ProductCharacteristics" />
                        </h2>
                        {parse(content.content || '', { trim: true })}
                     </StyledFirstContent>
                  </IntroWrapperEQ>
               )}
               {!content.categories.includes(8) && (
                  <IntroWrapper>
                     <StyledFirstContent>
                        {parse(content.content || '', { trim: true })}
                     </StyledFirstContent>
                     <StyledImage src={image} />
                  </IntroWrapper>
               )}
               <div>
                  {!!content.konstrukcja_nosna && (
                     <Section
                        id="konstrukcja_nosna"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.SupportingStructure'
                        })}
                     >
                        {parse(content.konstrukcja_nosna || '', { trim: true })}
                     </Section>
                  )}
                  {!!content.slup_z_automatem && (
                     <TwoCol>
                        {content.konstrukcja && (
                           <Section
                              id="konstrukcja"
                              headline={intl.formatMessage({
                                 id: 'SingleProductView.Construction'
                              })}
                           >
                              {parse(content.konstrukcja || '', { trim: true })}
                           </Section>
                        )}
                        <Section
                           id="slup_z_automatem"
                           headline={intl.formatMessage({
                              id: 'SingleProductView.PoleWithAutomaticMachine'
                           })}
                        >
                           {parse(content.slup_z_automatem || '', { trim: true })}
                        </Section>
                     </TwoCol>
                  )}
                  {content.wypelnienie && content.wypelnienie?.wypelnienie_text && (
                     <Section
                        id="wypelnienie"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.Filling'
                        })}
                     >
                        <>
                           {parse(content.wypelnienie?.wypelnienie_text || '', {
                              trim: true
                           })}
                           {parse(content.wypelnienie?.wypelnienie_table || '', {
                              trim: true
                           })}
                        </>
                     </Section>
                  )}
                  {content.wypelnienie && typeof content.wypelnienie === 'string' && (
                     <Section
                        id="wypelnienie"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.Filling'
                        })}
                     >
                        {parse(content.wypelnienie || '', { trim: true })}
                     </Section>
                  )}
                  {!!content.elementy_konstrukcyjne &&
                     typeof content.elementy_konstrukcyjne === 'string' && (
                        <Section
                           id="elementy_konstrukcyjne"
                           headline={intl.formatMessage({
                              id: 'SingleProductView.ConstructionElements'
                           })}
                        >
                           {parse(content.elementy_konstrukcyjne || '', { trim: true })}
                        </Section>
                     )}
                  {!!content.elementy_konstrukcyjne &&
                     typeof content.elementy_konstrukcyjne === 'object' && (
                        <SectionTwoCol
                           id="elementy_konstrukcyjne"
                           headline={intl.formatMessage({
                              id: 'SingleProductView.ConstructionElements'
                           })}
                        >
                           <div>
                              {parse(
                                 content.elementy_konstrukcyjne.elementy_konstrukcyjne_lewa || '',
                                 { trim: true }
                              )}
                           </div>
                           <div>
                              {parse(
                                 content.elementy_konstrukcyjne.elementy_konstrukcyjne_prawa || '',
                                 { trim: true }
                              )}
                           </div>
                        </SectionTwoCol>
                     )}
                  {!!content.informacje && typeof content.informacje === 'string' && (
                     <Section
                        id="informacje"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.Information'
                        })}
                     >
                        {parse(content.informacje || '', { trim: true })}
                     </Section>
                  )}
                  {!!content.informacje && typeof content.informacje === 'object' && (
                     <SectionTwoCol
                        id="informacje"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.Information'
                        })}
                     >
                        <div>{parse(content.informacje.informacje_lewa || '', { trim: true })}</div>
                        <div>
                           {parse(content.informacje.informacje_prawa || '', { trim: true })}
                        </div>
                     </SectionTwoCol>
                  )}
                  {!!content.dlugosc_bram && (
                     <Section
                        id="dlugosc_bram"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.GateLength'
                        })}
                     >
                        {parse(content.dlugosc_bram || '', { trim: true })}
                     </Section>
                  )}
                  {!!content.dostepne_wysokosci && (
                     <Section
                        id="dostepne_wysokosci"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.AdditionalInformation'
                        })}
                     >
                        {parse(content.dostepne_wysokosci || '', { trim: true })}
                     </Section>
                  )}
                  {!!content.wysokosc_bram__slupka && (
                     <Section
                        id="wysokosc_bram__slupka"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.PostsHeight'
                        })}
                     >
                        {parse(content.wysokosc_bram__slupka || '', { trim: true })}
                     </Section>
                  )}
                  {!!content.przekroj_slupka && (
                     <Section
                        id="przekroj_slupka"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.PostCrossSection'
                        })}
                     >
                        {parse(content.przekroj_slupka || '', { trim: true })}
                     </Section>
                  )}
                  {!!content.ilosc_pol_w_swietle && (
                     <Section
                        id="ilosc_pol_w_swietle"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.LightNumberFields'
                        })}
                     >
                        {parse(content.ilosc_pol_w_swietle || '', { trim: true })}
                     </Section>
                  )}
                  {!!content.przeciwwagi_do_bram?.length && (
                     <Section
                        id="przeciwwagi_do_bram"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.CounterweightStyle'
                        })}
                     >
                        <GridImage>
                           {content.przeciwwagi_do_bram?.map((item) => (
                              <BoxImage
                                 key={Math.random() + item?.przeciwwagi_do_bram_img?.ID}
                                 image={item?.przeciwwagi_do_bram_img?.url || ''}
                                 headline={item?.przeciwwagi_do_bram_description || ''}
                              />
                           ))}
                        </GridImage>
                     </Section>
                  )}
                  {!!content.waga && (
                     <Section
                        id="waga"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.Weight'
                        })}
                     >
                        {parse(content.waga || '', { trim: true })}
                     </Section>
                  )}
                  {!!content.ilosc_nitow && (
                     <Section
                        id="ilosc_nitow"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.NumberOfRivets'
                        })}
                     >
                        {parse(content.ilosc_nitow || '', { trim: true })}
                     </Section>
                  )}
                  {!!content.informacje_techniczne && (
                     <Section
                        id="informacje_techniczne"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.TechnicalInformations'
                        })}
                     >
                        {parse(content.informacje_techniczne || '', { trim: true })}
                     </Section>
                  )}
                  {!!content.montaz && typeof content.montaz === 'string' && (
                     <Section
                        id="montaz"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.Assembly'
                        })}
                     >
                        {parse(content.montaz || '', { trim: true })}
                     </Section>
                  )}
                  {!!content.montaz && typeof content.montaz !== 'string' && (
                     <Section
                        id="montaz"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.Assembly'
                        })}
                     >
                        <GridImage>
                           {content.montaz.montaz_img &&
                              content.montaz.montaz_img.map((item) => (
                                 <BoxImage key={Math.random() + item.ID} image={item.url || ''} />
                              ))}
                        </GridImage>
                        <div>{parse(content.montaz.montaz_description || '', { trim: true })}</div>
                     </Section>
                  )}
                  {!!content.konstrukcja && (
                     <Section
                        id="konstrukcja"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.Construction'
                        })}
                     >
                        {parse(content.konstrukcja || '', { trim: true })}
                     </Section>
                  )}
                  {!!content.informator && (
                     <Section
                        id="informator"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.Informant'
                        })}
                     >
                        {parse(content.informator || '', { trim: true })}
                     </Section>
                  )}
                  {!!content.podzial_slupkow && (
                     <Section
                        id="podzial_slupkow"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.BarsDivision'
                        })}
                     >
                        {parse(content.podzial_slupkow || '', { trim: true })}
                     </Section>
                  )}
                  {!!content.slupki_do_bram_uchylnych && (
                     <Section
                        id="slupki_do_bram_uchylnych"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.PolesForGate'
                        })}
                     >
                        {parse(content.slupki_do_bram_uchylnych || '', { trim: true })}
                     </Section>
                  )}
                  {!!content.rodzaje_przesel?.length && (
                     <Section
                        id="rodzaje_przesel"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.TypesOfSpans'
                        })}
                     >
                        <GridImage>
                           {content.rodzaje_przesel.map((item) => (
                              <Image
                                 src={item?.rodzaje_przesel_img?.url || ''}
                                 alt="image"
                                 key={Math.random()}
                              />
                           ))}
                        </GridImage>
                     </Section>
                  )}
                  {!!content.rodzaje_przesel_z_opisem?.length && (
                     <Section
                        id="rodzaje_przesel_z_opisem"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.TypesOfSpans'
                        })}
                     >
                        <GridImage>
                           {content.rodzaje_przesel_z_opisem?.map((item) => (
                              <BoxImage
                                 key={Math.random() + item?.rodzaje_przesel_z_opisem_img?.ID}
                                 image={item?.rodzaje_przesel_z_opisem_img?.url || ''}
                                 headline={item?.rodzaje_przesel_z_opisem_desc || ''}
                              />
                           ))}
                        </GridImage>
                     </Section>
                  )}
                  {!!content.akcesoria_do_furtek && (
                     <Section
                        id="akcesoria_do_furtek"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.AccessoriesForGateway'
                        })}
                     >
                        {parse(content.akcesoria_do_furtek || '', { trim: true })}
                     </Section>
                  )}
                  {!!content.akcesoria?.length && (
                     <Section
                        id="akcesoria"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.Accessories'
                        })}
                     >
                        {!!content.akcesoria_opis && (
                           <div>{parse(content.akcesoria_opis, { trim: true })}</div>
                        )}
                        <GridImage>
                           {content.akcesoria?.map((item) => (
                              <BoxImage
                                 key={Math.random() + item?.akcesoria_img?.ID}
                                 image={item?.akcesoria_img?.url || ''}
                                 headline={item?.akcesoria_description || ''}
                              />
                           ))}
                        </GridImage>
                     </Section>
                  )}
                  {!!content.akcesoria_druga_linia?.length && (
                     <Section
                        id="akcesoria_2"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.Accessories'
                        })}
                     >
                        {!!content.akcesoria_druga_linia_opis && (
                           <div>{parse(content.akcesoria_druga_linia_opis, { trim: true })}</div>
                        )}
                        <GridImage>
                           {content.akcesoria_druga_linia?.map((item) => (
                              <BoxImage
                                 key={Math.random() + item?.akcesoria_img?.ID}
                                 image={item?.akcesoria_img?.url || ''}
                                 headline={item?.akcesoria_description || ''}
                              />
                           ))}
                        </GridImage>
                     </Section>
                  )}
                  {!!content.akcesoria_trzecia_linia?.length && (
                     <Section
                        id="akcesoria_3"
                        headline={intl.formatMessage({
                           id: 'SingleProductView.Accessories'
                        })}
                     >
                        {!!content.akcesoria_trzecia_linia_opis && (
                           <div>{parse(content.akcesoria_trzecia_linia_opis, { trim: true })}</div>
                        )}
                        <GridImage>
                           {content.akcesoria_trzecia_linia?.map((item) => (
                              <BoxImage
                                 key={Math.random() + item?.akcesoria_img?.ID}
                                 image={item?.akcesoria_img?.url || ''}
                                 headline={item?.akcesoria_description || ''}
                              />
                           ))}
                        </GridImage>
                     </Section>
                  )}
               </div>
            </InnerWrapper>
         ) : (
            <Loader />
         )}
      </SidebarTemplate>
   );
};

const StyledFirstContent = styled.div`
   width: 100%;
   max-width: 100%;

   ${devices.tabletXL} {
      max-width: 700px;
      align-self: flex-start;
   }

   ${devices.desktop} {
      max-width: 800px;
      align-self: flex-start;
   }

   ${devices.desktopHD} {
      max-width: 1100px;
      align-self: flex-start;
   }

   .wp-block-table {
      width: 100%;
      overflow: hidden;
      overflow-x: scroll;
   }

   table {
      /* width: 100%; */
      /* max-width: 100%; */
      margin-bottom: 1rem;
      font-size: 14px;
      color: ${colors.neutral70};
      border-collapse: collapse;
      table-layout: fixed;
   }

   th {
      padding: 0.75rem;

      &:not(:first-of-type) {
         border-left: 1px solid ${colors.white};
      }
   }

   tbody {
      tr {
         &:nth-of-type(even) {
            background-color: ${colors.neutral10};
         }
      }
   }

   td {
      padding: 0.75rem;

      &:not(:first-of-type) {
         border-left: 1px solid ${colors.neutral20};
      }
   }
`;

const StyledImage = styled(Image)`
   margin-top: 20px;

   ${devices.tablet} {
      margin: 20px 0 0;
   }

   ${devices.desktopHD} {
      margin-left: 0;
      width: 70%;
   }

   ${devices.desktopFHD} {
      width: 66%;
   }
`;

const IntroWrapper = styled.div`
   ${devices.tablet} {
      display: flex;
      align-items: center;
      flex-direction: column;
   }

   ${devices.tabletXL} {
      margin-top: 20px;
   }

   ${devices.desktopHD} {
      margin-top: 30px;
   }

   ${devices.desktopFHD} {
      margin-top: 40px;
   }
`;

const IntroWrapperEQ = styled.div`
   h2 {
      font-size: 24px;
   }

   ${devices.tablet} {
      display: flex;
      justify-content: space-between;
      flex-direction: row;
      & > div {
         width: 50%;
      }
   }

   ${devices.tabletXL} {
      margin-top: 20px;
   }

   ${devices.desktopHD} {
      margin-top: 30px;
   }

   ${devices.desktopFHD} {
      margin-top: 40px;
   }
`;

const InnerWrapper = styled.div`
   margin-top: 10px;

   ${devices.desktopHD} {
      margin: 0;
   }
`;

const TwoCol = styled.div`
   display: flex;
   justify-content: space-between;
   & > section {
      width: 100%;
   }

   ${devices.tablet} {
      & > section {
         width: calc(50% - 15px);
      }
   }
`;
